<script setup lang="ts">
  import { type Middleware } from '@floating-ui/dom';
  import * as floating from '@floating-ui/vue';
  import type { EnumValues } from '@travelata-libs/utils';
  import { onClickOutside } from '@vueuse/core';
  import { type HTMLAttributes, computed, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue';
  import Teleport from 'vue2-teleport'; // eslint-disable-line @typescript-eslint/no-unused-vars
  import { FloatingTrigger, FloatingPlacement } from './Floating.types';
  import { horizontalPlacements, verticalPlacements, allowedHorizontalReplacements } from './lib';
  import { useTrigger } from '@/composables';

  interface FloatingElementProps {
    trigger?: EnumValues<typeof FloatingTrigger>;
    visible?: boolean;
    placement?: EnumValues<typeof FloatingPlacement>;
    offset?: number;
    virtualRef?: HTMLElement | null;
    hideDelay?: number;
    teleported?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
  }

  interface FloatingElementEmits {
    (e: 'change-visible', value: boolean): void;
    (e: 'hide'): void;
  }

  const props = withDefaults(defineProps<FloatingElementProps>(), {
    trigger: FloatingTrigger.Manual,
    visible: false,
    placement: FloatingPlacement.Bottom,
    offset: 8,
    hideDelay: 300,
    teleported: false,
    virtualRef: null,
    disabled: false,
    fullWidth: false,
  });

  const emit = defineEmits<FloatingElementEmits>();

  const slots = useSlots();

  const containerElement = ref<HTMLElement>();
  const referenceElement = ref<HTMLElement>();
  const floatingElement = ref<HTMLElement>();
  const arrowElement = ref<HTMLElement>();

  const isHovered = ref(false);
  const isClicked = ref(false);

  const hideDelayId = ref<ReturnType<typeof setTimeout>>();

  const isVisible = computed(() => {
    return isHovered.value || isClicked.value || props.visible;
  });

  const reference = computed(() => {
    return props.virtualRef || referenceElement.value;
  });

  const updateFloatingWidth = (floatingWidth: number, availableWidth: number) => {
    if (floatingElement.value) {
      const minWidth = Math.min(floatingWidth, availableWidth);

      floatingElement.value.style.minWidth = `${minWidth}px`;
      floatingElement.value.style.maxWidth = `100vw`;
    }
  };

  const middleware = computed(() => {
    const middlewares: Middleware[] = [];

    if (slots.arrow) {
      middlewares.push(floating.arrow({ element: arrowElement, padding: 10 }));
    }

    middlewares.push(
      floating.size({
        apply({ rects, availableWidth }) {
          if (floatingElement.value) {
            updateFloatingWidth(rects.floating.width, availableWidth);
          }
        },
      }),
    );

    if (verticalPlacements.includes(props.placement)) {
      middlewares.push(floating.flip(), floating.shift({ padding: 0 }));
    }

    if (horizontalPlacements.includes(props.placement)) {
      middlewares.push(
        floating.flip({ fallbackPlacements: allowedHorizontalReplacements[props.placement] }),
        floating.shift({ limiter: floating.limitShift(), padding: 0 }),
      );
    }

    middlewares.push(floating.offset(props.offset), floating.hide());

    return middlewares;
  });

  const floatingConfig = computed(() => {
    const { strategy, x, y, middlewareData, placement } = floating.useFloating(reference, floatingElement, {
      placement: props.placement,
      middleware,
      whileElementsMounted: floating.autoUpdate,
    });
    return { strategy, x, y, middlewareData, placement };
  });

  const floatingStyle = computed<HTMLAttributes['style']>(() => {
    return {
      position: floatingConfig.value.strategy.value,
      top: `${floatingConfig.value.y.value ?? 0}px`,
      left: `${floatingConfig.value.x.value ?? 0}px`,
    };
  });

  const placementSide = computed(() => {
    return floatingConfig.value.placement.value.split('-')[0];
  });

  const arrowTransform = computed(() => {
    if (placementSide.value === FloatingPlacement.Top) {
      return 'rotate(180deg)';
    }

    if (placementSide.value === FloatingPlacement.Left || placementSide.value === FloatingPlacement.Right) {
      const arrowRect = arrowElement.value?.getBoundingClientRect();

      if (arrowRect) {
        const arrowShift = Math.abs((arrowRect.width - arrowRect.height) / 2);
        let angle = 90;

        if (placementSide.value === FloatingPlacement.Right) {
          angle *= -1;
        }

        return `rotate(${angle}deg) translateY(${arrowShift}px)`;
      }
    }

    return '';
  });

  const arrowStyle = computed<HTMLAttributes['style']>(() => {
    return {
      position: 'absolute',
      left:
        typeof floatingConfig.value.middlewareData.value.arrow?.x === 'number'
          ? `${floatingConfig.value.middlewareData.value.arrow.x}px`
          : '',
      top:
        typeof floatingConfig.value.middlewareData.value.arrow?.y === 'number'
          ? `${floatingConfig.value.middlewareData.value.arrow.y}px`
          : '',
      [placementSide.value]: 'calc(100% - 1px)',
      transform: arrowTransform.value,
    };
  });

  const onClick = () => {
    if (props.disabled) {
      return;
    }

    isClicked.value = !isClicked.value;
  };

  const onHover = () => {
    if (props.disabled) {
      return;
    }

    if (hideDelayId.value) {
      clearTimeout(hideDelayId.value);
    }

    isHovered.value = true;
  };

  const onHoverEnd = () => {
    hideDelayId.value = setTimeout(() => {
      isHovered.value = false;
    }, props.hideDelay);
  };

  const open = () => {
    if (props.disabled || isClicked.value) {
      return;
    }

    isClicked.value = true;
  };

  const hide = () => {
    isClicked.value = false;
    isHovered.value = false;
  };

  const { initTrigger, destroyTrigger } = useTrigger({
    referenceElement,
    floatingElement,
    callback: {
      onHover,
      onHoverEnd,
      onClick,
    },
  });

  watch(
    () => props.trigger,
    () => {
      destroyTrigger();
      initTrigger(props.trigger);
    },
  );

  watch(
    isVisible,
    (value) => {
      if (!value) {
        emit('hide');
      }
      emit('change-visible', value);
    },
    {
      immediate: true,
    },
  );

  onMounted(() => {
    initTrigger(props.trigger);

    onClickOutside(
      containerElement,
      () => {
        hide();
      },
      {
        ignore: [floatingElement],
      },
    );
  });

  onBeforeUnmount(() => {
    destroyTrigger();
  });

  defineExpose({
    hide,
    open,
  });
</script>

<template>
  <div
    ref="containerElement"
    :class="['tl-floating', `tl-floating_trigger-${trigger}`, { 'tl-floating_full-width': fullWidth }]"
  >
    <div ref="referenceElement" data-test-id="reference" class="tl-floating__reference">
      <slot name="reference" />
    </div>
    <Teleport to="body" :disabled="!teleported">
      <transition :name="`slide-${placementSide}`" mode="out-in">
        <div v-if="isVisible" ref="floatingElement" class="tl-floating__container" :style="floatingStyle">
          <div v-if="$slots.arrow" ref="arrowElement" class="tl-floating__arrow" :style="arrowStyle">
            <slot name="arrow" />
          </div>
          <slot name="floating" />
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
  .tl-floating {
    $root: &;

    &_full-width {
      #{$root}__container {
        width: 100%;
      }
    }

    &_trigger-click {
      #{$root}__reference {
        cursor: pointer;
      }
    }

    &__arrow {
      display: flex;
    }
  }
</style>
